// src/pages/FundPlanner/components/Funds/Positions.jsx

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Pencil, Plus, Trash2, Upload } from "lucide-react";
import ExcelGrid from "components/ExcelGrid";
import PositionModal from "./PositionModal";
import ImportStructureModal from "./ImportStructureModal";
import { useFunds } from "pages/FundPlanner/context/FundsContext";
import { roundToCents } from "pages/FundPlanner/lib/money";
import { useDialog } from "context/DialogContext";
import {
  translateStructureToPositionDrafts,
  findDuplicateImportedPositions,
  getStructureFundDefaults,
} from "./structureImport";
import {
  TYPE_OPTIONS,
  TYPE_VALUE_SET,
  isCusipInvestmentType,
} from "./investmentTypes";

/* ────────────────────────────────
   Constants
──────────────────────────────── */
const COLUMN_KEYS = [
  "investment_type",
  "cusip_or_id",
  "par_amount",
  "purchase_price",
  "purchase_date",
  "settlement_date",
];

/* ────────────────────────────────
   Helpers
──────────────────────────────── */
function normalizeType(v) {
  const value = v ? String(v).trim() : "";
  return TYPE_VALUE_SET.has(value) ? value : "";
}

function isUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value ?? "")
  );
}

function isPersistedPositionRow(row) {
  return isUuid(row?.id);
}

function isBlankPositionRow(row) {
  if (!row) return true;

  return ![
    row.investment_type,
    row.cusip_or_id,
    row.par_amount,
    row.current_face,
    row.units,
    row.balance_amount,
    row.purchase_price,
    row.rate,
    row.day_count_basis,
    row.interest_frequency,
    row.maturity_date,
    row.purchase_date,
    row.settlement_date,
    row.closed_date,
    row.issuer,
    row.description,
    row.notes,
    row.initial_rating_rollup,
  ].some((value) => String(value ?? "").trim() !== "");
}

function isGridRowEditable(row, isReadOnly) {
  return !isReadOnly && !isPersistedPositionRow(row);
}

function defaultPurchasePriceForType(investmentType, value = "") {
  const type = String(investmentType ?? "").trim();
  return type && !isCusipInvestmentType(type) && value === "" ? 100 : value;
}

function buildRow(payload = {}) {
  const investmentType = normalizeType(payload.investment_type);

  return {
    id:
      payload.id ||
      `pos_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    investment_type: investmentType,
    cusip_or_id: payload.cusip_or_id || "",
    par_amount: payload.par_amount ?? "",
    current_face: payload.current_face ?? "",
    units: payload.units ?? "",
    balance_amount: payload.balance_amount ?? "",
    latest_valuation: payload.latest_valuation ?? null,
    latest_rating_valuation: payload.latest_rating_valuation ?? null,
    latest_security_master_valuation: payload.latest_security_master_valuation ?? null,
    latest_priced_valuation: payload.latest_priced_valuation ?? null,
    purchase_price: defaultPurchasePriceForType(
      investmentType,
      payload.purchase_price ?? ""
    ),
    original_cost: payload.original_cost ?? null,
    purchase_accrued_interest: payload.purchase_accrued_interest ?? "",
    rate: payload.raw_coupon ?? payload.rate ?? "",
    day_count_basis: payload.day_count_basis || "",
    interest_frequency: payload.interest_frequency || "",
    maturity_date: payload.raw_maturity_date || payload.maturity_date || "",
    security_maturity_date: payload.security_maturity_date || "",
    purchase_date: payload.purchase_date || "",
    settlement_date: payload.settlement_date || "",
    closed_date: payload.closed_date || "",
    issuer: payload.raw_issuer_name || payload.issuer || "",
    description: payload.raw_security_description || payload.description || "",
    notes: payload.notes || "",
    source_of_funds_position_id:
      payload.source_of_funds_position_id || "",
    initial_rating_rollup: payload.initial_rating_rollup || "",
    initial_rating_rollup_explicit:
      payload.initial_rating_rollup_explicit ?? false,
    source_type: payload.source_type || "",
    external_position_id: payload.external_position_id || "",
    __isNew: payload.__isNew ?? false,
  };
}

function applyInitialPositionAmount(row) {
  if (!row?.initial_rating_rollup_explicit) return row;

  const amount = row.par_amount ?? "";

  if (isCusipInvestmentType(row.investment_type)) {
    return {
      ...row,
      current_face: amount,
    };
  }

  return {
    ...row,
    units: amount,
    balance_amount: amount,
  };
}

function applyInitialPositionToHistory(row, historyRows = []) {
  if (!row?.initial_rating_rollup_explicit) return historyRows;

  const amount = row.par_amount ?? "";
  const isCusipType = isCusipInvestmentType(row.investment_type);

  return (Array.isArray(historyRows) ? historyRows : []).map((entry) => {
    if (entry?.source_type !== "initial_position") return entry;

    return {
      ...entry,
      par_amount: amount,
      current_face: isCusipType ? amount : "",
      units: isCusipType ? "" : amount,
      balance_amount: isCusipType ? "" : amount,
      price: isCusipType ? row.purchase_price ?? "" : "",
      rating_rollup: row.initial_rating_rollup ?? entry.rating_rollup ?? "",
    };
  });
}

function firstPresent(...values) {
  return values.find((value) => value !== "" && value != null) ?? "";
}

function numberOrNull(value) {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function historyDate(row) {
  return row?.history_date || row?.valuation_date || row?.activity_date || "";
}

function compareHistoryDesc(a, b) {
  const dateCompare = String(historyDate(b)).localeCompare(String(historyDate(a)));
  if (dateCompare) return dateCompare;

  return String(b?.created_at || "").localeCompare(String(a?.created_at || ""));
}

function isAfterHistoryAnchor(row, anchor) {
  if (!anchor) return true;

  const dateCompare = String(historyDate(row)).localeCompare(
    String(historyDate(anchor))
  );
  if (dateCompare) return dateCompare > 0;

  return String(row?.created_at || "").localeCompare(
    String(anchor?.created_at || "")
  ) > 0;
}

function derivePositionAmountsFromHistory(row = {}, historyRows = []) {
  const rows = Array.isArray(historyRows) ? historyRows : [];
  const latestValuation =
    rows
      .filter((entry) => entry?.history_type === "valuation" || entry?.valuation_date)
      .sort(compareHistoryDesc)[0] || null;
  const initialPositionValuation =
    rows
      .filter((entry) => entry?.source_type === "initial_position")
      .filter(
        (entry) =>
          numberOrNull(entry.current_face) !== null ||
          numberOrNull(entry.par_amount) !== null ||
          numberOrNull(entry.units) !== null ||
          numberOrNull(entry.balance_amount) !== null
      )
      .sort(compareHistoryDesc)[0] || null;
  const amountAnchor =
    rows
      .filter(
        (entry) =>
          (entry?.history_type === "valuation" || entry?.valuation_date) &&
          entry?.source_type !== "initial_position" &&
          (numberOrNull(entry.current_face) !== null ||
            numberOrNull(entry.units) !== null ||
            numberOrNull(entry.balance_amount) !== null)
      )
      .sort(compareHistoryDesc)[0] || null;

  let currentFace =
    numberOrNull(amountAnchor?.current_face) ??
    numberOrNull(initialPositionValuation?.current_face) ??
    numberOrNull(initialPositionValuation?.par_amount) ??
    numberOrNull(row.par_amount) ??
    numberOrNull(row.current_face);
  let units =
    numberOrNull(amountAnchor?.units) ??
    numberOrNull(initialPositionValuation?.units) ??
    numberOrNull(initialPositionValuation?.par_amount) ??
    numberOrNull(row.par_amount) ??
    numberOrNull(row.units);
  let balanceAmount =
    numberOrNull(amountAnchor?.balance_amount) ??
    numberOrNull(initialPositionValuation?.balance_amount) ??
    numberOrNull(initialPositionValuation?.units) ??
    numberOrNull(initialPositionValuation?.par_amount) ??
    numberOrNull(row.par_amount) ??
    numberOrNull(row.balance_amount);
  balanceAmount = roundToCents(balanceAmount);

  rows
    .filter((entry) => entry?.history_type === "activity" || entry?.activity_date)
    .filter((entry) => isAfterHistoryAnchor(entry, amountAnchor))
    .sort((a, b) => -compareHistoryDesc(a, b))
    .forEach((entry) => {
      const parAfter = numberOrNull(entry.par_after);
      const unitsAfter = numberOrNull(entry.units_after);
      const balanceAfter = numberOrNull(entry.balance_after);
      const parDelta = numberOrNull(entry.par_delta);
      const unitsDelta = numberOrNull(entry.units_delta);

      if (parAfter !== null) {
        currentFace = parAfter;
      } else if (parDelta !== null && currentFace !== null) {
        currentFace += parDelta;
      }

      if (unitsAfter !== null) {
        units = unitsAfter;
      } else if (unitsDelta !== null && units !== null) {
        units += unitsDelta;
      }

      if (balanceAfter !== null) {
        balanceAmount = roundToCents(balanceAfter);
      } else if (unitsDelta !== null && balanceAmount !== null) {
        balanceAmount = roundToCents(balanceAmount + roundToCents(unitsDelta));
      }
    });

  return {
    latestValuation,
    currentFace,
    units,
    balanceAmount,
  };
}

function latestRatingValuation(historyRows = []) {
  return (
    (Array.isArray(historyRows) ? historyRows : [])
      .filter((entry) => entry?.history_type === "valuation" || entry?.valuation_date)
      .filter((entry) => String(entry?.rating_rollup ?? "").trim())
      .sort(compareHistoryDesc)[0] || null
  );
}

function isSaleActivity(row) {
  return (
    row?.activity_type === "sale" ||
    row?.activity_type === "partial_sale" ||
    row?.activity_type === "redemption" ||
    row?.activity_type === "principal_paydown"
  );
}

function latestSaleDate(historyRows = []) {
  const sale = (Array.isArray(historyRows) ? historyRows : [])
    .filter((row) => row?.history_type === "activity" || row?.activity_date)
    .filter(isSaleActivity)
    .sort(compareHistoryDesc)[0];

  return sale?.activity_date || sale?.history_date || "";
}

function isZeroish(value) {
  const n = Number(value);
  return Number.isFinite(n) && Math.abs(n) < 0.005;
}

function isZeroBalance(row, derivedAmounts) {
  const amount = isCusipInvestmentType(row?.investment_type)
    ? derivedAmounts.currentFace
    : firstPresent(derivedAmounts.balanceAmount, derivedAmounts.units);

  return isZeroish(amount);
}

function getCurrentAmount(row) {
  if (isCusipInvestmentType(row?.investment_type)) {
    return firstPresent(row?.current_face, row?.par_amount);
  }

  return firstPresent(
    row?.balance_amount,
    row?.units,
    row?.current_face,
    row?.par_amount
  );
}

function toIsoDate(value) {
  if (value == null) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${y}-${m}-${d}`;
  }

  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, m, d, y] = slashMatch;
    return `${y}-${String(Number(m)).padStart(2, "0")}-${String(
      Number(d)
    ).padStart(2, "0")}`;
  }

  const dashUsMatch = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dashUsMatch) {
    const [, m, d, y] = dashUsMatch;
    return `${y}-${String(Number(m)).padStart(2, "0")}-${String(
      Number(d)
    ).padStart(2, "0")}`;
  }

  return raw;
}

function addDaysIso(dateText, days) {
  const base = dateText ? new Date(`${dateText}T00:00:00`) : new Date();
  if (Number.isNaN(base.getTime())) return "";
  base.setDate(base.getDate() + days);
  return base.toISOString().slice(0, 10);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function isSlgsDemandDepositType(value) {
  const type = String(value ?? "").trim().toLowerCase();
  return type === "slgs demand deposit" || type === "demand deposit";
}

function isSlgsBalanceActivityType(value) {
  return value === "deposit" || value === "withdrawal";
}

function earliestIsoDate(values = []) {
  return values
    .map(toIsoDate)
    .filter(Boolean)
    .sort()[0] || "";
}

function isSoldPosition(row) {
  const closedDate = toIsoDate(row?.closed_date);
  if (!closedDate) return false;
  return closedDate <= todayIso();
}

function isMaturedPosition(row) {
  const maturityDate = toIsoDate(row?.maturity_date);
  if (!maturityDate) return false;
  return maturityDate <= todayIso();
}

function coerceCellValue(key, value) {
  const raw = value == null ? "" : String(value).trim();

  if (key === "investment_type") {
    return normalizeType(raw);
  }

  if (
    key === "purchase_date" ||
    key === "settlement_date" ||
    key === "maturity_date"
  ) {
    return raw ? toIsoDate(raw) : "";
  }

  if (key === "par_amount" || key === "purchase_price" || key === "rate") {
    if (!raw) return "";
    const cleaned = raw.replace(/[^\d.-]/g, "");
    return cleaned === "" ? "" : Number(cleaned);
  }

  return raw;
}

function parseClipboard(text) {
  return String(text ?? "")
    .split(/\r?\n/)
    .filter((line) => line.length > 0)
    .map((line) => line.split("\t"));
}

const fmt2 = (v) =>
  v !== "" && v != null
    ? Number(v).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "";

const fmt8 = (v) =>
  v !== "" && v != null
    ? Number(v).toLocaleString(undefined, {
        minimumFractionDigits: 8,
        maximumFractionDigits: 8,
      })
    : "";

/* ────────────────────────────────
   Component
──────────────────────────────── */
export default function Positions() {
  const {
    currentId,
    currentName,
    description,
    portfolioId,
    portfolios,
    positions,
    setDescription,
    setPositions,
    setPositionsFromCommittedHistory,
    makeBlankPosition,
    isLoadingFund,
    isReadOnly,
    isDirty,
    savePositionActivity,
    saveManualTransfer,
    savePositionValuation,
    updatePositionActivity,
    updateManualTransfer,
    updatePositionValuation,
    deletePositionActivity,
    deletePositionValuation,
    loadPositionHistory,
    loadTransferDestinationPositions,
    generateSlgsDemandAccruals,
    getSlgsDemandAccrualRecalcRange,
    recalculateSlgsDemandAccruals,
  } = useFunds();
  const { confirm, alert } = useDialog();

  const [selectionState, setSelectionState] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showClosedPositions, setShowClosedPositions] = useState(false);
  const [positionHistory, setPositionHistory] = useState([]);
  const [isLoadingPositionHistory, setIsLoadingPositionHistory] =
    useState(false);
  const [transferDestinations, setTransferDestinations] = useState([]);
  const [isLoadingTransferDestinations, setIsLoadingTransferDestinations] =
    useState(false);

  /* ────────────────────────────────
     Sorted Rows
  ──────────────────────────────── */
  const sortedRows = useMemo(() => {
    const safe = (v) => (v == null ? "" : String(v));

    return positions
      .filter((row) => !isReadOnly || isPersistedPositionRow(row))
      .sort((a, b) => {
        const persistedA = isPersistedPositionRow(a);
        const persistedB = isPersistedPositionRow(b);
        if (persistedA !== persistedB) return persistedA ? -1 : 1;
        if (!persistedA) return 0;

        const typeA = safe(a.investment_type).toLowerCase();
        const typeB = safe(b.investment_type).toLowerCase();
        if (typeA < typeB) return -1;
        if (typeA > typeB) return 1;

        const settleA = safe(a.settlement_date);
        const settleB = safe(b.settlement_date);
        if (settleA < settleB) return -1;
        if (settleA > settleB) return 1;

        const cusipA = safe(a.cusip_or_id).toLowerCase();
        const cusipB = safe(b.cusip_or_id).toLowerCase();
        if (cusipA < cusipB) return -1;
        if (cusipA > cusipB) return 1;

        return 0;
      });
  }, [isReadOnly, positions]);

  const displayedRows = useMemo(() => {
    const visiblePositions = showClosedPositions
      ? sortedRows
      : sortedRows.filter((row) => !isSoldPosition(row));

    return visiblePositions;
  }, [showClosedPositions, sortedRows]);

  const handleGridChange = useCallback(
    (updatedRows) => {
      if (isReadOnly) return;

      const safeUpdatedRows = Array.isArray(updatedRows) ? updatedRows : [];

      setPositions((prev) => {
        const originalById = new Map(
          (Array.isArray(prev) ? prev : [])
            .filter((row) => isPersistedPositionRow(row))
            .map((row) => [row.id, row])
        );

        const nextVisibleRows = safeUpdatedRows.map((row) => {
          if (isPersistedPositionRow(row)) {
            return originalById.get(row.id) || row;
          }

          const investmentType = normalizeType(row.investment_type);

          return {
            ...row,
            investment_type: investmentType,
            purchase_price: defaultPurchasePriceForType(
              investmentType,
              row.purchase_price ?? ""
            ),
          };
        });

        if (showClosedPositions) return nextVisibleRows;

        const hiddenClosedRows = (Array.isArray(prev) ? prev : []).filter(
          (row) => isSoldPosition(row)
        );
        return [...nextVisibleRows, ...hiddenClosedRows];
      });
    },
    [isReadOnly, setPositions, showClosedPositions]
  );

  const handleAddEntryRow = useCallback(
    (rowId) => {
      if (isReadOnly) return;

      setPositions((prev) => {
        const next = [...(Array.isArray(prev) ? prev : [])];
        const rowIndex = next.findIndex((row) => row.id === rowId);
        next.splice(
          rowIndex >= 0 ? rowIndex + 1 : next.length,
          0,
          makeBlankPosition()
        );
        return next;
      });
    },
    [isReadOnly, makeBlankPosition, setPositions]
  );

  const handleDeleteEntryRow = useCallback(
    (rowId) => {
      if (isReadOnly) return;
      setPositions((prev) => {
        const remainingRows = (Array.isArray(prev) ? prev : []).filter(
          (row) => row.id !== rowId
        );
        const hasUnsavedRow = remainingRows.some(
          (row) => !isPersistedPositionRow(row)
        );

        return hasUnsavedRow
          ? remainingRows
          : [...remainingRows, makeBlankPosition()];
      });
    },
    [isReadOnly, makeBlankPosition, setPositions]
  );

  const handlePasteCapture = useCallback(
    (e) => {
      if (isReadOnly) return;

      const raw = e.clipboardData?.getData("text");
      if (!raw) return;

      const selected = selectionState?.selected;
      if (
        !selected ||
        typeof selected.row !== "number" ||
        typeof selected.col !== "number"
      ) {
        return;
      }

      const grid = parseClipboard(raw);
      if (!grid.length) return;

      e.preventDefault();
      e.stopPropagation();

      const startRow = selected.row;
      const startCol = selected.col;

      setPositions((prev) => {
        const next = [...prev];

        grid.forEach((cols, rIdx) => {
          const rowIdx = startRow + rIdx;
          const visibleRow = displayedRows[rowIdx];

          if (isPersistedPositionRow(visibleRow)) {
            return;
          }

          let targetIdx = visibleRow?.id
            ? next.findIndex((row) => row.id === visibleRow.id)
            : -1;

          if (targetIdx === -1 && !isPersistedPositionRow(visibleRow)) {
            targetIdx = next.findIndex((row) => isBlankPositionRow(row));
          }

          while (targetIdx === -1) {
            next.push(makeBlankPosition());
            targetIdx = next.length - 1;
          }

          const row = { ...next[targetIdx] };

          cols.forEach((val, cIdx) => {
            const colIdx = startCol + cIdx;
            const key = COLUMN_KEYS[colIdx];
            if (!key) return;

            row[key] = coerceCellValue(key, val);
          });

          const investmentType = normalizeType(row.investment_type);

          next[targetIdx] = {
            ...row,
            investment_type: investmentType,
            purchase_price: defaultPurchasePriceForType(
              investmentType,
              row.purchase_price ?? ""
            ),
          };
        });

        return next;
      });
    },
    [displayedRows, isReadOnly, makeBlankPosition, selectionState, setPositions]
  );

  const performDelete = useCallback(
    (rowId) => {
      if (isReadOnly) return;

      setPositions((prev) => prev.filter((row) => row.id !== rowId));
      setEditingRowId(null);
      setShowModal(false);
    },
    [isReadOnly, setPositions]
  );

  const handleModalDelete = useCallback(async () => {
    if (isReadOnly) return;
    if (!editingRowId) return;

    const ok = await confirm(
      "This position will be removed when you select Save Fund.",
      {
        title: "Remove Position from Fund",
        okLabel: "Remove",
        cancelLabel: "Cancel",
      }
    );

    if (!ok) return;
    performDelete(editingRowId);
  }, [confirm, editingRowId, isReadOnly, performDelete]);

  const openAdd = useCallback(() => {
    if (isReadOnly) return;

    setEditingRowId(null);
    setShowModal(true);
  }, [isReadOnly]);

  const openEdit = useCallback((row) => {
    setEditingRowId(row.id);
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setEditingRowId(null);
    setShowModal(false);
    setPositionHistory([]);
    setIsLoadingPositionHistory(false);
    setTransferDestinations([]);
    setIsLoadingTransferDestinations(false);
  }, []);

  const editingRow = useMemo(
    () => positions.find((r) => r.id === editingRowId) || null,
    [positions, editingRowId]
  );
  const editingRowIsPersisted = isUuid(editingRow?.id);
  const currentPortfolioName = useMemo(
    () =>
      (Array.isArray(portfolios) ? portfolios : []).find(
        (portfolio) => portfolio.id === portfolioId
      )?.name || "",
    [portfolioId, portfolios]
  );
  const modalInitialValues = useMemo(
    () =>
      editingRow
        ? {
            positionId: editingRow.id,
            fundId: currentId,
            fundName: currentName,
            portfolioId,
            portfolioName: currentPortfolioName,
            investmentType: editingRow.investment_type,
            cusipOrId: editingRow.cusip_or_id,
            description: editingRow.description,
            amount: editingRow.par_amount,
            purchasePrice: editingRow.purchase_price,
            originalCost: editingRow.original_cost,
            purchaseAccruedInterest: editingRow.purchase_accrued_interest,
            rate: editingRow.rate,
            dayCountBasis: editingRow.day_count_basis,
            interestFrequency: editingRow.interest_frequency,
            maturityDate: editingRow.maturity_date,
            securityMaturityDate: editingRow.security_maturity_date,
            tradeDate: editingRow.purchase_date,
            settlementDate: editingRow.settlement_date,
            closedDate: editingRow.closed_date,
            issuerName: editingRow.issuer,
            notes: editingRow.notes,
            sourceOfFundsPositionId:
              editingRow.source_of_funds_position_id || "",
            currentAmount: getCurrentAmount(editingRow),
            latestValuationDate:
              editingRow.latest_priced_valuation?.valuation_date || "",
            latestValuationPrice:
              editingRow.latest_priced_valuation?.price ?? "",
          }
        : null,
    [currentId, currentName, currentPortfolioName, editingRow, portfolioId]
  );

  const refreshPositionHistory = useCallback(
    async (positionId) => {
      if (!positionId || !isUuid(positionId)) {
        setPositionHistory([]);
        return { ok: true, data: [] };
      }

      setIsLoadingPositionHistory(true);
      const result = await loadPositionHistory(positionId);
      setIsLoadingPositionHistory(false);

      if (result.ok) {
        const historyRows = applyInitialPositionToHistory(
          editingRow,
          result.data
        );
        setPositionHistory(historyRows);
        return { ...result, data: historyRows };
      } else {
        setPositionHistory([]);
        console.error("Position history load failed:", result.error);
      }

      return result;
    },
    [editingRow, loadPositionHistory]
  );

  useEffect(() => {
    if (!showModal || !editingRowIsPersisted || !editingRow?.id) {
      setPositionHistory([]);
      return;
    }

    refreshPositionHistory(editingRow.id);
  }, [editingRow?.id, editingRowIsPersisted, refreshPositionHistory, showModal]);

  useEffect(() => {
    if (
      !showModal ||
      (editingRowIsPersisted && !editingRow?.id)
    ) {
      setTransferDestinations([]);
      return;
    }

    let cancelled = false;
    setIsLoadingTransferDestinations(true);
    loadTransferDestinationPositions({
      sourcePositionId: editingRowIsPersisted ? editingRow.id : "",
    }).then(
      (result) => {
        if (cancelled) return;
        setIsLoadingTransferDestinations(false);
        setTransferDestinations(result.ok ? result.data : []);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [
    editingRow?.id,
    editingRowIsPersisted,
    loadTransferDestinationPositions,
    showModal,
  ]);

  const requirePersistedEditingPosition = useCallback(async () => {
    if (isDirty) {
      const msg =
        "Save the fund before recording activity or valuation updates. These updates are committed immediately.";
      await alert(msg, { title: "Save Fund First" });
      return "";
    }

    if (editingRow?.id && isUuid(editingRow.id)) {
      return editingRow.id;
    }

    const msg =
      "Save the fund before recording activity or valuation updates for this position.";
    console.error(msg, { positionId: editingRow?.id || editingRowId || "" });
    await alert(msg, { title: "Save Fund First" });
    return "";
  }, [alert, editingRow?.id, editingRowId, isDirty]);

  const applyHistoryToPosition = useCallback(
    (positionId, historyRows) => {
      setPositionsFromCommittedHistory((prev) =>
        prev.map((row) => {
          if (row.id !== positionId) return row;

          const derivedAmounts = derivePositionAmountsFromHistory(
            row,
            historyRows
          );
          const latestValuation = derivedAmounts.latestValuation;
          const saleDate = latestSaleDate(historyRows);
          const shouldClose = saleDate && isZeroBalance(row, derivedAmounts);

          return {
            ...row,
            latest_valuation: latestValuation || null,
            latest_rating_valuation: latestRatingValuation(historyRows),
            latest_priced_valuation:
              latestValuation?.price != null && latestValuation.price !== ""
                ? {
                    source: "position",
                    valuation_date: latestValuation.valuation_date || "",
                    price: latestValuation.price,
                    created_at: latestValuation.created_at || "",
                  }
                : row.latest_priced_valuation ?? null,
            current_face: derivedAmounts.currentFace ?? "",
            units: derivedAmounts.units ?? "",
            balance_amount: derivedAmounts.balanceAmount ?? "",
            closed_date: shouldClose
              ? saleDate
              : saleDate
              ? ""
              : row.closed_date,
          };
        })
      );
    },
    [setPositionsFromCommittedHistory]
  );

  const refreshAffectedPositions = useCallback(
    async (primaryPositionId, affectedPositionIds = []) => {
      const ids = Array.from(
        new Set([primaryPositionId, ...(affectedPositionIds || [])].filter(Boolean))
      );

      for (const positionId of ids) {
        if (!positions.some((row) => row.id === positionId)) continue;

        const historyResult =
          positionId === primaryPositionId
            ? await refreshPositionHistory(positionId)
            : await loadPositionHistory(positionId);

        if (historyResult.ok) {
          const targetRow =
            positionId === editingRow?.id
              ? editingRow
              : positions.find((row) => row.id === positionId);
          const historyRows = applyInitialPositionToHistory(
            targetRow,
            historyResult.data
          );
          applyHistoryToPosition(positionId, historyRows);
        }
      }
    },
    [
      applyHistoryToPosition,
      editingRow,
      loadPositionHistory,
      positions,
      refreshPositionHistory,
    ]
  );

  const handleModalSave = useCallback(
    (payload, options = {}) => {
      if (isReadOnly) return { ok: false, error: "Read-only fund." };

      if (editingRowId) {
        setPositions((prev) =>
          prev.map((r) =>
            r.id === editingRowId
              ? applyInitialPositionAmount({
                  ...r,
                  ...buildRow({
                    ...r,
                    ...payload,
                  }),
                  id: r.id,
                })
              : r
          )
        );
      } else {
        setPositions((prev) => [
          applyInitialPositionAmount(
            buildRow({
              ...payload,
              __isNew: true,
            })
          ),
          ...prev,
        ]);
      }

      if (!options.keepOpen) {
        closeModal();
      }

      return { ok: true };
    },
    [editingRowId, closeModal, isReadOnly, setPositions]
  );

  const maybeRecalculateSlgsDemandAccrualsFromDate = useCallback(
    async (positionId, fromDate, message) => {
      if (!isSlgsDemandDepositType(editingRow?.investment_type)) return null;
      if (!fromDate) return null;

      const rangeResult = await getSlgsDemandAccrualRecalcRange(positionId, {
        fromDate,
      });

      if (!rangeResult.ok) {
        console.error(
          "SLGS Demand Deposit recalculation range check failed:",
          rangeResult.error
        );
        await alert(
          rangeResult.error ||
            "Unable to check whether generated daily interest needs recalculation.",
          { title: "Interest Recalculation Not Checked" }
        );
        return rangeResult;
      }

      const range = Array.isArray(rangeResult.data)
        ? rangeResult.data[0]
        : null;
      if (!range?.end_date) return { ok: true, skipped: true };

      const shouldRecalculate = await confirm(
        message ||
          `This change affects generated SLGS Demand Deposit daily interest from ${range.start_date} through ${range.end_date}. Recalculate those Interest Earned entries now?`,
        {
          title: "Recalculate Daily Interest",
          okLabel: "Recalculate",
          cancelLabel: "Skip",
        }
      );

      if (!shouldRecalculate) {
        return { ok: false, error: "Interest recalculation skipped." };
      }

      const recalcResult = await recalculateSlgsDemandAccruals(positionId, {
        fromDate: range.start_date,
        endDate: range.end_date,
      });

      if (!recalcResult.ok) {
        console.error(
          "SLGS Demand Deposit interest recalculation failed:",
          recalcResult.error
        );
        await alert(
          recalcResult.error ||
            "Unable to recalculate SLGS Demand Deposit daily interest.",
          { title: "Interest Not Recalculated" }
        );
        return recalcResult;
      }

      const summary = Array.isArray(recalcResult.data)
        ? recalcResult.data[0]
        : null;
      await alert(
        `Recalculated ${summary?.days_recalculated ?? 0} daily interest entr${
          Number(summary?.days_recalculated) === 1 ? "y" : "ies"
        }.`,
        { title: "Interest Recalculated" }
      );

      return recalcResult;
    },
    [
      alert,
      confirm,
      editingRow?.investment_type,
      getSlgsDemandAccrualRecalcRange,
      recalculateSlgsDemandAccruals,
    ]
  );

  const maybeRecalculateSlgsDemandAccruals = useCallback(
    async (positionId, payload = {}, meta = {}) => {
      const payloads = Array.isArray(payload.activities)
        ? payload.activities
        : [payload];
      const balanceActivityDates = payloads
        .filter((activity) => isSlgsBalanceActivityType(activity?.activity_type))
        .map((activity) => activity?.activity_date);

      if (
        meta.previousActivityDate &&
        isSlgsBalanceActivityType(meta.previousActivityType)
      ) {
        balanceActivityDates.push(meta.previousActivityDate);
      }

      const affectedActivityDate = earliestIsoDate(balanceActivityDates);
      if (!affectedActivityDate) return null;

      const recalcStartDate = addDaysIso(affectedActivityDate, 1);
      if (!recalcStartDate) return null;

      return maybeRecalculateSlgsDemandAccrualsFromDate(
        positionId,
        recalcStartDate
      );
    },
    [maybeRecalculateSlgsDemandAccrualsFromDate]
  );

  const handleSaveActivity = useCallback(
    async (payload, meta = {}) => {
      if (isReadOnly) return { ok: false, error: "Read-only fund." };

      const positionId = await requirePersistedEditingPosition();
      if (!positionId) {
        return {
          ok: false,
          error:
            "Save the fund before recording activity or valuation updates for this position.",
        };
      }

      const result = await savePositionActivity(positionId, payload);
      if (!result.ok) {
        console.error("Position activity save failed:", result.error);
        await alert(result.error || "Unable to save position activity.", {
          title: "Activity Not Saved",
        });
      } else {
        await maybeRecalculateSlgsDemandAccruals(positionId, payload, meta);
        const historyResult = await refreshPositionHistory(positionId);
        applyHistoryToPosition(positionId, historyResult.data);
      }

      return result;
    },
    [
      alert,
      isReadOnly,
      maybeRecalculateSlgsDemandAccruals,
      refreshPositionHistory,
      applyHistoryToPosition,
      requirePersistedEditingPosition,
      savePositionActivity,
    ]
  );

  const handleSaveTransfer = useCallback(
    async (payload) => {
      if (isReadOnly) return { ok: false, error: "Read-only fund." };

      const positionId = await requirePersistedEditingPosition();
      if (!positionId) {
        return {
          ok: false,
          error:
            "Save the fund before recording activity or valuation updates for this position.",
        };
      }

      const result = await saveManualTransfer(payload.source_position_id || positionId, {
        ...payload,
        source_position_id: payload.source_position_id || positionId,
        destination_position_id: payload.destination_position_id || positionId,
      });

      if (!result.ok) {
        console.error("Transfer save failed:", result.error);
        await alert(result.error || "Unable to save transfer.", {
          title: "Transfer Not Saved",
        });
      } else {
        await refreshAffectedPositions(positionId, result.affectedPositionIds);
      }

      return result;
    },
    [
      alert,
      isReadOnly,
      refreshAffectedPositions,
      requirePersistedEditingPosition,
      saveManualTransfer,
    ]
  );

  const handleSaveValuation = useCallback(
    async (payload, meta = {}) => {
      if (isReadOnly) return { ok: false, error: "Read-only fund." };

      const positionId = await requirePersistedEditingPosition();
      if (!positionId) {
        return {
          ok: false,
          error:
            "Save the fund before recording activity or valuation updates for this position.",
        };
      }

      const result = await savePositionValuation(positionId, payload);
      if (!result.ok) {
        console.error("Position valuation save failed:", result.error);
        await alert(result.error || "Unable to save position valuation.", {
          title: "Valuation Not Saved",
        });
      } else {
        if (payload?.source_type === "initial_position") {
          const recalcStartDate = earliestIsoDate([
            payload.valuation_date,
            meta.previousValuationDate,
          ]);
          await maybeRecalculateSlgsDemandAccrualsFromDate(
            positionId,
            recalcStartDate,
            `This initial position change affects generated SLGS Demand Deposit daily interest beginning ${recalcStartDate}. Recalculate affected Interest Earned entries now?`
          );
        }
        const historyResult = await refreshPositionHistory(positionId);
        applyHistoryToPosition(positionId, historyResult.data);
      }

      return result;
    },
    [
      alert,
      isReadOnly,
      maybeRecalculateSlgsDemandAccrualsFromDate,
      refreshPositionHistory,
      applyHistoryToPosition,
      requirePersistedEditingPosition,
      savePositionValuation,
    ]
  );

  const handleGenerateSlgsDemandAccruals = useCallback(
    async ({ endDate } = {}) => {
      if (isReadOnly) return { ok: false, error: "Read-only fund." };

      const positionId = await requirePersistedEditingPosition();
      if (!positionId) {
        return {
          ok: false,
          error:
            "Save the fund before generating SLGS Demand Deposit interest for this position.",
        };
      }

      const shouldGenerate = await confirm(
        "Please ensure that all Deposits or Withdrawals are entered prior to generating interest.",
        {
          title: "Generate Daily Interest",
          okLabel: "Generate",
          cancelLabel: "Cancel",
        }
      );

      if (!shouldGenerate) {
        return { ok: false, error: "Interest generation cancelled." };
      }

      const result = await generateSlgsDemandAccruals(positionId, { endDate });
      if (!result.ok) {
        console.error("SLGS Demand Deposit interest generation failed:", result.error);
        await alert(
          result.error || "Unable to generate SLGS Demand Deposit interest.",
          { title: "Interest Not Generated" }
        );
      } else {
        const historyResult = await refreshPositionHistory(positionId);
        applyHistoryToPosition(positionId, historyResult.data);

        const summary = Array.isArray(result.data) ? result.data[0] : null;
        await alert(
          `Generated ${summary?.days_generated ?? 0} daily interest entr${
            Number(summary?.days_generated) === 1 ? "y" : "ies"
          }. ${summary?.days_existing ?? 0} already existed.`,
          { title: "Interest Generated" }
        );
      }

      return result;
    },
    [
      alert,
      applyHistoryToPosition,
      confirm,
      generateSlgsDemandAccruals,
      isReadOnly,
      refreshPositionHistory,
      requirePersistedEditingPosition,
    ]
  );

  const handleUpdateActivity = useCallback(
    async (activityId, payload, meta = {}) => {
      if (isReadOnly) return { ok: false, error: "Read-only fund." };

      const positionId = await requirePersistedEditingPosition();
      if (!positionId) {
        return {
          ok: false,
          error:
            "Save the fund before recording activity or valuation updates for this position.",
        };
      }

      const result = await updatePositionActivity(activityId, payload);
      if (!result.ok) {
        console.error("Position activity update failed:", result.error);
        await alert(result.error || "Unable to update position activity.", {
          title: "Activity Not Updated",
        });
      } else {
        await maybeRecalculateSlgsDemandAccruals(positionId, payload, meta);
        const historyResult = await refreshPositionHistory(positionId);
        applyHistoryToPosition(positionId, historyResult.data);
      }

      return result;
    },
    [
      alert,
      isReadOnly,
      maybeRecalculateSlgsDemandAccruals,
      refreshPositionHistory,
      applyHistoryToPosition,
      requirePersistedEditingPosition,
      updatePositionActivity,
    ]
  );

  const handleUpdateTransfer = useCallback(
    async (activityId, payload) => {
      if (isReadOnly) return { ok: false, error: "Read-only fund." };

      const positionId = await requirePersistedEditingPosition();
      if (!positionId) {
        return {
          ok: false,
          error:
            "Save the fund before recording activity or valuation updates for this position.",
        };
      }

      const result = await updateManualTransfer(activityId, payload);
      if (!result.ok) {
        console.error("Transfer update failed:", result.error);
        await alert(result.error || "Unable to update transfer.", {
          title: "Transfer Not Updated",
        });
      } else {
        await refreshAffectedPositions(positionId, result.affectedPositionIds);
      }

      return result;
    },
    [
      alert,
      isReadOnly,
      refreshAffectedPositions,
      requirePersistedEditingPosition,
      updateManualTransfer,
    ]
  );

  const handleUpdateValuation = useCallback(
    async (valuationId, payload, meta = {}) => {
      if (isReadOnly) return { ok: false, error: "Read-only fund." };

      const positionId = await requirePersistedEditingPosition();
      if (!positionId) {
        return {
          ok: false,
          error:
            "Save the fund before recording activity or valuation updates for this position.",
        };
      }

      const result = await updatePositionValuation(valuationId, payload);
      if (!result.ok) {
        console.error("Position valuation update failed:", result.error);
        await alert(result.error || "Unable to update position valuation.", {
          title: "Valuation Not Updated",
        });
      } else {
        if (
          payload?.source_type === "initial_position" ||
          meta.previousSourceType === "initial_position"
        ) {
          const recalcStartDate = earliestIsoDate([
            payload.valuation_date,
            meta.previousValuationDate,
          ]);
          await maybeRecalculateSlgsDemandAccrualsFromDate(
            positionId,
            recalcStartDate,
            `This initial position change affects generated SLGS Demand Deposit daily interest beginning ${recalcStartDate}. Recalculate affected Interest Earned entries now?`
          );
        }
        const historyResult = await refreshPositionHistory(positionId);
        applyHistoryToPosition(positionId, historyResult.data);
      }

      return result;
    },
    [
      alert,
      applyHistoryToPosition,
      isReadOnly,
      maybeRecalculateSlgsDemandAccrualsFromDate,
      refreshPositionHistory,
      requirePersistedEditingPosition,
      updatePositionValuation,
    ]
  );

  const handleDeleteHistoryEntry = useCallback(
    async (entry) => {
      if (isReadOnly) return { ok: false, error: "Read-only fund." };

      const positionId = await requirePersistedEditingPosition();
      if (!positionId) {
        return {
          ok: false,
          error:
            "Save the fund before recording activity or valuation updates for this position.",
        };
      }

      const isTransferEntry = entry?.source_type === "manual_transfer";
      const ok = await confirm(
        isTransferEntry
          ? "This permanently deletes both sides of this transfer. This action cannot be undone."
          : "This permanently deletes this activity history entry. This action cannot be undone.",
        {
          title: isTransferEntry ? "Delete Transfer" : "Delete History Entry",
          okLabel: "Delete",
          cancelLabel: "Cancel",
        }
      );

      if (!ok) return { ok: false, error: "Delete cancelled." };

      const result =
        entry?.history_type === "valuation"
          ? await deletePositionValuation(entry.source_id || entry.id)
          : await deletePositionActivity(entry?.source_id || entry?.id);

      if (!result.ok) {
        console.error("Position history delete failed:", result.error);
        await alert(result.error || "Unable to delete history entry.", {
          title: "History Entry Not Deleted",
        });
      } else {
        if (
          entry?.history_type === "activity" &&
          isSlgsBalanceActivityType(entry?.activity_type)
        ) {
          await maybeRecalculateSlgsDemandAccrualsFromDate(
            positionId,
            addDaysIso(entry.activity_date, 1)
          );
        }

        if (
          entry?.history_type === "valuation" &&
          entry?.source_type === "initial_position"
        ) {
          await maybeRecalculateSlgsDemandAccrualsFromDate(
            positionId,
            toIsoDate(entry.valuation_date || entry.history_date),
            `This initial position deletion affects generated SLGS Demand Deposit daily interest beginning ${toIsoDate(
              entry.valuation_date || entry.history_date
            )}. Recalculate affected Interest Earned entries now?`
          );
        }

        await refreshAffectedPositions(positionId, [
          entry?.position_id,
          entry?.transfer_counterparty?.position_id,
        ]);
      }

      return result;
    },
    [
      alert,
      confirm,
      deletePositionActivity,
      deletePositionValuation,
      isReadOnly,
      maybeRecalculateSlgsDemandAccrualsFromDate,
      refreshAffectedPositions,
      requirePersistedEditingPosition,
    ]
  );

  const handleImportStructure = useCallback(
    async (structure) => {
      if (isReadOnly) return;

      const drafts = translateStructureToPositionDrafts(structure, {
        fundId: currentId,
      });

      const importedRows = drafts.map((draft) =>
        buildRow({
          ...draft,
          __isNew: true,
        })
      );

      const duplicates = findDuplicateImportedPositions(positions, importedRows);
      if (duplicates.length > 0) {
        await alert(
          "One or more positions from this structure already exist in the current working set. Import was not completed.",
          { title: "Duplicate Imported Positions" }
        );
        return;
      }

      const fundDefaults = getStructureFundDefaults(structure);
      if (fundDefaults.description && !String(description ?? "").trim()) {
        setDescription(fundDefaults.description);
      }

      setPositions((prev) => [...importedRows, ...prev]);
      setShowImportModal(false);
    },
    [
      alert,
      currentId,
      description,
      isReadOnly,
      positions,
      setDescription,
      setPositions,
    ]
  );

  const columns = useMemo(
    () => [
      {
        key: "investment_type",
        label: "Type",
        editable: (row) => isGridRowEditable(row, isReadOnly),
        editor: "select",
        options: TYPE_OPTIONS,
        placeholder: "Select type...",
        width: "w-40",
      },
      {
        key: "cusip_or_id",
        label: "CUSIP / ID",
        editable: (row) => isGridRowEditable(row, isReadOnly),
        className: "font-mono-app",
        width: "w-40",
      },
      {
        key: "par_amount",
        label: "Amount",
        editable: (row) => isGridRowEditable(row, isReadOnly),
        numeric: true,
        format: (_value, row) => fmt2(getCurrentAmount(row)),
        className: "font-mono-app text-right",
        width: "w-32",
      },
      {
        key: "purchase_price",
        label: "Purchase Price",
        editable: (row) => isGridRowEditable(row, isReadOnly),
        numeric: true,
        format: fmt8,
        className: "font-mono-app text-right",
        width: "w-32",
      },
      {
        key: "purchase_date",
        label: "Trade Date",
        editable: (row) => isGridRowEditable(row, isReadOnly),
        date: true,
        editor: "date",
        className: "font-mono-app",
        width: "w-32",
      },
      {
        key: "settlement_date",
        label: "Settlement",
        editable: (row) => isGridRowEditable(row, isReadOnly),
        date: true,
        editor: "date",
        className: "font-mono-app",
        width: "w-32",
      },
      {
        key: "actions",
        label: "",
        editable: false,
        width: "w-20",
        render: (row, rows) => {
          const sold = isSoldPosition(row);
          const matured = isMaturedPosition(row);
          const isUnsaved = !isPersistedPositionRow(row);
          const unsavedRows = rows.filter(
            (candidate) => !isPersistedPositionRow(candidate)
          );
          const lastUnsavedRow = unsavedRows[unsavedRows.length - 1];
          const isLastUnsaved = isUnsaved && lastUnsavedRow?.id === row.id;
          const canDeleteUnsavedRow =
            isUnsaved &&
            !(unsavedRows.length === 1 && isBlankPositionRow(row));

          return (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => openEdit(row)}
                className="text-app hover:text-brand"
                title={isReadOnly ? "View in form" : "Edit in form"}
              >
                <Pencil size={16} />
              </button>

              {!isReadOnly && isLastUnsaved ? (
                <button
                  type="button"
                  onClick={() => handleAddEntryRow(row.id)}
                  className="text-green-600 hover:text-green-800"
                  title="Add row below"
                  aria-label="Add row below"
                >
                  <Plus size={16} />
                </button>
              ) : null}

              {!isReadOnly && canDeleteUnsavedRow ? (
                <button
                  type="button"
                  onClick={() => handleDeleteEntryRow(row.id)}
                  className="text-red-500 hover:text-red-700"
                  title="Delete row"
                  aria-label="Delete row"
                >
                  <Trash2 size={16} />
                </button>
              ) : null}

              {sold ? (
                <span className="text-xs text-muted font-medium tracking-wide">
                  CLOSED
                </span>
              ) : null}

              {matured ? (
                <span className="text-xs text-muted font-medium tracking-wide">
                  MATURED
                </span>
              ) : null}
            </div>
          );
        },
      },
    ],
    [handleAddEntryRow, handleDeleteEntryRow, isReadOnly, openEdit]
  );

  if (!currentId) {
    return <div className="text-sm text-muted">Save fund first.</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex justify-between items-center">
          <h3 className="text-app font-semibold">Edit Positions</h3>

          <div className="flex items-center gap-2">
            {!isReadOnly ? (
              <>
                <button
                  className="btn btn-ghost"
                  onClick={() => setShowImportModal(true)}
                >
                  <span className="inline-flex items-center gap-2">
                    <Upload size={16} />
                    <span>Import FP Structure</span>
                  </span>
                </button>

                <button className="btn btn-primary" onClick={openAdd}>
                  Add Position
                </button>
              </>
            ) : null}
          </div>
        </div>

      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="text-muted text-sm">
          Enter positions directly in the table below or click Add Position for
          more detailed entry options.
        </div>

        <label className="inline-flex items-center gap-2 text-sm text-app cursor-pointer">
          <input
            type="checkbox"
            checked={showClosedPositions}
            onChange={(event) => setShowClosedPositions(event.target.checked)}
          />
          <span>Show closed positions</span>
        </label>
      </div>

      <div onPasteCapture={handlePasteCapture}>
        <ExcelGrid
          columns={columns}
          rows={displayedRows}
          onChange={isReadOnly ? undefined : handleGridChange}
          onSelectionChange={setSelectionState}
        />
      </div>

      <div className="text-muted text-sm">
        Details of all new CUSIP positions are updated from Security Master data
        within two (2) business days after initial entry.
      </div>

      <PositionModal
        isOpen={showModal}
        onClose={closeModal}
        onSave={handleModalSave}
        onDelete={editingRow && !isReadOnly ? handleModalDelete : null}
        onSaveActivity={handleSaveActivity}
        onSaveTransfer={handleSaveTransfer}
        onSaveValuation={handleSaveValuation}
        onUpdateActivity={handleUpdateActivity}
        onUpdateTransfer={handleUpdateTransfer}
        onUpdateValuation={handleUpdateValuation}
        onDeleteHistoryEntry={handleDeleteHistoryEntry}
        onGenerateSlgsDemandAccruals={handleGenerateSlgsDemandAccruals}
        activityHistory={positionHistory}
        transferDestinations={transferDestinations}
        isLoadingTransferDestinations={isLoadingTransferDestinations}
        isLoadingActivityHistory={isLoadingPositionHistory}
        isPersistedPosition={editingRowIsPersisted}
        readOnly={isReadOnly}
        initialValues={modalInitialValues}
        onSmartDocComplete={async () => {
          if (!editingRow?.id) return;
          const historyResult = await refreshPositionHistory(editingRow.id);
          applyHistoryToPosition(editingRow.id, historyResult.data);
        }}
      />

      <ImportStructureModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportStructure}
      />

      {isLoadingFund ? (
        <div className="text-xs text-muted">Loading...</div>
      ) : null}
    </div>
  );
}
